import React, {useEffect, useMemo, useState} from 'react'
import {
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    SortingState,
    useReactTable,
} from '@tanstack/react-table'
import moment from 'moment'
import TitleComponent from '../../components/TitleComponent/TitleComponent'
import {getUtovarKamiona, UtovarKamionaItem} from '../../api/proizvodnja'

type ActiveTab = 'danas' | 'utovareno'

const getCardColors = (status?: string | null) => {
    const normalizedStatus = status?.toUpperCase() ?? ''

    if (normalizedStatus.includes('C')) {
        return {
            background: 'linear-gradient(135deg, #b91c1c 0%, #ef4444 100%)',
            shadow: '0 12px 24px rgba(185, 28, 28, 0.18)',
        }
    }

    if (normalizedStatus.includes('U')) {
        return {
            background: 'linear-gradient(135deg, #047857 0%, #22c55e 100%)',
            shadow: '0 12px 24px rgba(4, 120, 87, 0.18)',
        }
    }

    return {
        background: 'linear-gradient(135deg, #334155 0%, #64748b 100%)',
        shadow: '0 12px 24px rgba(51, 65, 85, 0.16)',
    }
}

const formatDate = (date?: string | null) => (date ? moment(date).format('DD-MM-YYYY HH:mm') : '-')

const formatSelectedDate = (date: string) => moment(date).format('DD-MM-YYYY')

const formatWaitingTime = (start?: string | null, created?: string | null) => {
    if (!start || !created) {
        return '-'
    }

    const startTime = moment(start)
    const createdTime = moment(created)

    if (!startTime.isValid() || !createdTime.isValid()) {
        return '-'
    }

    const duration = moment.duration(startTime.diff(createdTime))
    const totalMinutes = Math.max(0, Math.floor(duration.asMinutes()))
    const hours = Math.floor(totalMinutes / 60)
    const minutes = totalMinutes % 60

    return `${hours}h ${minutes}min`
}

const hasStatus = (status: string | null | undefined, value: string) =>
    status?.toUpperCase().includes(value) ?? false

const hasKrajUtovara = (item: UtovarKamionaItem) => Boolean(item.KrajUtovara)

const TruckIcon = () => (
    <svg
        width='72'
        height='52'
        viewBox='0 0 72 52'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
        style={{
            position: 'absolute',
            right: 10,
            bottom: 8,
            opacity: 0.15,
        }}
    >
        <path
            d='M6 13C6 10.7909 7.79086 9 10 9H42C44.2091 9 46 10.7909 46 13V34H6V13Z'
            fill='white'
        />
        <path
            d='M46 19H58.3431C59.404 19 60.4214 19.4214 61.1716 20.1716L68 27V34H46V19Z'
            fill='white'
        />
        <path d='M55 22H59.5L64 27H55V22Z' fill='currentColor' opacity='0.25'/>
        <circle cx='20' cy='37' r='6' fill='white'/>
        <circle cx='57' cy='37' r='6' fill='white'/>
        <circle cx='20' cy='37' r='2.5' fill='currentColor' opacity='0.35'/>
        <circle cx='57' cy='37' r='2.5' fill='currentColor' opacity='0.35'/>
    </svg>
)

const InfoBlock = ({
                       label,
                       value,
                       bold = false,
                   }: {
    label: string
    value?: string | number | null
    bold?: boolean
}) => (
    <div className='mb-2'>
        <div className='text-white-50 fw-semibold fs-9 text-uppercase mb-1'>{label}</div>
        <div
            className={`${bold ? 'fw-bold' : 'fw-semibold'} text-break`}
            style={{fontSize: bold ? 15 : 14, lineHeight: 1.25}}
        >
            {value || '-'}
        </div>
    </div>
)

const TruckCard = ({item, index}: { item: UtovarKamionaItem; index: number }) => {
    const colors = getCardColors(item.Status)
    const key = item.Id ?? item.id ?? `${item.RedniBroj}-${index}`
    const izdatBroj = item.IzdatBroj ?? formatDate(item.Kreirano)
    const pocetakUtovara = item.PocetakUtovara ?? item.PocetakUtvoara

    return (
        <div className='col-12 col-sm-6 col-lg-4 col-xl-3' key={key}>
            <div
                className='d-flex flex-column text-white p-4 rounded-4 h-100 position-relative overflow-hidden'
                style={{
                    minHeight: 245,
                    background: colors.background,
                    boxShadow: colors.shadow,
                }}
            >
                <TruckIcon/>

                <div className='d-flex justify-content-between align-items-start gap-2 position-relative mb-4'>
                    <div>
                        <div className='text-white-50 fw-semibold text-uppercase fs-9 mb-1'>
                            Redni broj
                        </div>
                        <div className='fw-bolder' style={{fontSize: 34, lineHeight: 1}}>
                            {item.RedniBroj ?? '-'}
                        </div>
                    </div>
                </div>
                <div className='position-relative mt-auto'>
                    {item.Kupac && <InfoBlock label='Kupac' value={item.Kupac} bold/>}
                    <InfoBlock label='Vozac' value={item.Vozac}/>
                    <InfoBlock label='Utovara' value={item.uposlenik?.UposlenikImePrezime}/>
                    <InfoBlock label='Izdat broj' value={izdatBroj}/>
                    <InfoBlock label='Pocetak utovara' value={formatDate(pocetakUtovara)}/>
                    <InfoBlock label='Kraj utovara' value={formatDate(item.KrajUtovara)}/>
                    <InfoBlock label='Vrijeme cekanja' value={formatWaitingTime(pocetakUtovara, item.Kreirano)}/>
                </div>
            </div>
        </div>
    )
}

const getPocetakUtovara = (item: UtovarKamionaItem) => item.PocetakUtovara ?? item.PocetakUtvoara

const getIzdatBroj = (item: UtovarKamionaItem) => item.IzdatBroj ?? formatDate(item.Kreirano)

const UtovarenoTable = ({data}: { data: UtovarKamionaItem[] }) => {
    const [globalFilter, setGlobalFilter] = useState('')
    const [sorting, setSorting] = useState<SortingState>([])

    const columns = useMemo(
        () => [
            {
                id: 'RedniBroj',
                header: 'Redni broj',
                accessorFn: (row: UtovarKamionaItem) => row.RedniBroj ?? '-',
                cell: (info: any) => <span className='fw-bold text-gray-800'>{info.getValue()}</span>,
            },
            {
                id: 'Kupac',
                header: 'Kupac',
                accessorFn: (row: UtovarKamionaItem) => row.Kupac ?? '-',
                cell: (info: any) => <span className='fw-semibold text-gray-800'>{info.getValue()}</span>,
            },
            {
                id: 'Vozac',
                header: 'Vozac',
                accessorFn: (row: UtovarKamionaItem) => row.Vozac ?? '-',
            },
            {
                id: 'Utovara',
                header: 'Utovara',
                accessorFn: (row: UtovarKamionaItem) => row.uposlenik?.UposlenikImePrezime ?? '-',
            },
            {
                id: 'IzdatBroj',
                header: 'Izdat broj',
                accessorFn: (row: UtovarKamionaItem) => getIzdatBroj(row),
            },
            {
                id: 'PocetakUtovara',
                header: 'Pocetak utovara',
                accessorFn: (row: UtovarKamionaItem) => formatDate(getPocetakUtovara(row)),
            },
            {
                id: 'KrajUtovara',
                header: 'Kraj utovara',
                accessorFn: (row: UtovarKamionaItem) => formatDate(row.KrajUtovara),
            },
            {
                id: 'VrijemeCekanja',
                header: 'Vrijeme cekanja',
                accessorFn: (row: UtovarKamionaItem) => formatWaitingTime(getPocetakUtovara(row), row.Kreirano),
                cell: (info: any) => <span className='fw-semibold'>{info.getValue()}</span>,
            },
        ],
        []
    )

    const table = useReactTable({
        data,
        columns,
        state: {
            globalFilter,
            sorting,
        },
        onGlobalFilterChange: setGlobalFilter,
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
    })

    return (
        <>
            <div className='d-flex flex-column flex-md-row justify-content-start align-items-stretch align-items-md-center gap-3 mb-5'>
                <input
                    type='text'
                    className='form-control form-control-sm'
                    placeholder='Pretraga...'
                    value={globalFilter}
                    onChange={(event) => setGlobalFilter(event.target.value)}
                    style={{maxWidth: 320}}
                />
                <div className='fw-bold text-gray-700'>
                    Ukupno redova: {table.getFilteredRowModel().rows.length}
                </div>
            </div>
            <div className='table-responsive'>
                <table className='table align-middle table-bordered gy-5 no-footer' style={{fontSize: '11px'}}>
                    <thead>
                    {table.getHeaderGroups().map((headerGroup) => (
                        <tr
                            key={headerGroup.id}
                            className='text-start fw-bolder fs-7 text-uppercase gs-0 bg-dark p-2'
                        >
                            {headerGroup.headers.map((header, index) => (
                                <th
                                    key={header.id}
                                    className={`text-white ${index === 0 ? 'ps-4' : ''} ${index === headerGroup.headers.length - 1 ? 'pe-4 text-end' : ''} min-w-130px`}
                                    onClick={header.column.getToggleSortingHandler()}
                                    style={{cursor: header.column.getCanSort() ? 'pointer' : 'default'}}
                                >
                                    {header.isPlaceholder ? null : (
                                        <span className='d-inline-flex align-items-center gap-2'>
                                            {flexRender(header.column.columnDef.header, header.getContext())}
                                            {{
                                                asc: '▲',
                                                desc: '▼',
                                            }[header.column.getIsSorted() as string] ?? ''}
                                        </span>
                                    )}
                                </th>
                            ))}
                        </tr>
                    ))}
                    </thead>
                    <tbody>
                    {table.getRowModel().rows.length > 0 ? (
                        table.getRowModel().rows.map((row) => (
                            <tr key={row.id}>
                                {row.getVisibleCells().map((cell, index) => (
                                    <td
                                        key={cell.id}
                                        className={`${index === 0 ? 'ps-4' : ''} ${index === row.getVisibleCells().length - 1 ? 'pe-4 text-end' : ''}`}
                                    >
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </td>
                                ))}
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan={columns.length} className='text-center text-muted py-10'>
                                Nema rezultata za unesenu pretragu.
                            </td>
                        </tr>
                    )}
                    </tbody>
                </table>
            </div>
        </>
    )
}

const UtovarKamionaLista = () => {
    const [data, setData] = useState<UtovarKamionaItem[]>([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [activeTab, setActiveTab] = useState<ActiveTab>('danas')
    const [selectedDate, setSelectedDate] = useState(moment().subtract(1, 'day').format('YYYY-MM-DD'))

    const fetchData = async () => {
        setLoading(true)
        setError('')

        try {
            const response = await getUtovarKamiona(
                activeTab === 'utovareno'
                    ? {datum: selectedDate, utovareno: 1}
                    : {utovareno: 0}
            )
            setData(response)
        } catch (err) {
            setError('Greska prilikom ucitavanja kamiona za utovar.')
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchData()
    }, [activeTab, selectedDate])

    const naCekanjuTotal = useMemo(() => data.filter((item) => hasStatus(item.Status, 'C')).length, [data])
    const uUtovaruTotal = useMemo(() => data.filter((item) => hasStatus(item.Status, 'U')).length, [data])
    const danasNaUtovarData = useMemo(() => data.filter((item) => !hasKrajUtovara(item)), [data])

    return (
        <div className='card mb-5 mb-xl-10'>
            <div className='card-header border-0 flex-column flex-md-row align-items-stretch align-items-md-center gap-4 py-5'>
                <div className='card-title m-0'>
                    <TitleComponent name='Utovar Kamiona Paneli'/>
                </div>
                <div className='card-toolbar flex-wrap gap-3'>
                    <span className='badge badge-light-primary fs-7 px-4 py-3'>
                        Na cekanju: {naCekanjuTotal}
                    </span>
                    <span className='badge badge-light-success fs-7 px-4 py-3'>
                        U utovaru: {uUtovaruTotal}
                    </span>
                    <button
                        type='button'
                        className='btn btn-sm btn-light-primary'
                        onClick={fetchData}
                        disabled={loading}
                    >
                        {loading ? 'Ucitavanje...' : 'Osvjezi'}
                    </button>
                </div>
            </div>

            <div className='card-body border-top p-4 p-md-9'>
                <div className='d-flex flex-column flex-lg-row justify-content-between align-items-stretch align-items-lg-center gap-4 mb-8'>
                    <ul className='nav nav-tabs nav-line-tabs nav-line-tabs-2x border-0 fs-6 fw-bold'>
                        <li className='nav-item'>
                            <button
                                type='button'
                                className={`nav-link ${activeTab === 'danas' ? 'active' : ''}`}
                                onClick={() => setActiveTab('danas')}
                            >
                                Danas na utovaru
                            </button>
                        </li>
                        <li className='nav-item'>
                            <button
                                type='button'
                                className={`nav-link ${activeTab === 'utovareno' ? 'active' : ''}`}
                                onClick={() => setActiveTab('utovareno')}
                            >
                                Utovareno
                            </button>
                        </li>
                    </ul>

                    {activeTab === 'utovareno' && (
                        <div className='d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center gap-3'>
                            <label className='fw-semibold text-gray-700 mb-0'>Datum</label>
                            <input
                                type='date'
                                className='form-control form-control-sm'
                                value={selectedDate}
                                onChange={(event) => setSelectedDate(event.target.value)}
                                style={{minWidth: 180}}
                            />
                        </div>
                    )}
                </div>

                {activeTab === 'utovareno' && (
                    <div className='alert alert-primary d-flex flex-column flex-sm-row align-items-start align-items-sm-center gap-2 py-3 px-4 mb-6'>
                        <span className='fw-bold'>Odabran datum:</span>
                        <span>{formatSelectedDate(selectedDate)}</span>
                    </div>
                )}

                {error && (
                    <div className='alert alert-danger d-flex align-items-center p-5 mb-8'>
                        <span>{error}</span>
                    </div>
                )}

                {!loading && data.length === 0 && !error && (
                    <div className='text-center py-15'>
                        <div className='fw-bold fs-3 text-gray-700 mb-2'>
                            {activeTab === 'utovareno' ? 'Nema utovarenih kamiona' : 'Nema kamiona na cekanju'}
                        </div>
                        <div className='text-muted'>
                            {activeTab === 'utovareno'
                                ? 'Za odabrani datum nema zavrsenih utovara.'
                                : 'Trenutno nema aktivnih zapisa za utovar.'}
                        </div>
                    </div>
                )}

                {loading ? (
                    <div className='text-center py-15 fw-semibold text-muted'>Ucitavanje...</div>
                ) : activeTab === 'utovareno' ? (
                    <UtovarenoTable data={data}/>
                ) : (
                    <div className='row g-4'>
                        {danasNaUtovarData.map((item, index) => (
                            <TruckCard item={item} index={index} key={item.Id ?? item.id ?? `${item.RedniBroj}-${index}`}/>
                        ))}
                    </div>
                )}
            </div>
        </div>
    )
}

export default UtovarKamionaLista
